import { useState } from 'react';
import {
  accessRequestApprove,
  accessRequestDeny,
  adminFacilities,
  adminUserLogout,
  approvalPersonApprove,
  approvalPersonDecline,
  approvalsQueue,
  facilityBaaStatus,
  facilityOnboard,
  intelFacilities,
  type AccessRequestsResponse,
  type ApiError,
  type CatalogFacility,
  type PendingPerson,
} from '@aithora/shared';
import { useConsoleRole, useSession } from '../../session/SessionContext';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import { CAPT, ErrLine, I, btn, chip, pre, sx, useApi, useGuarded } from './kit';
import { CustomRolesPanel, HouseholdCreate } from './RolesHouseholds';

// Production features of Users & Permissions the V3 design draws no control for
// (Flutter users_view.dart, access_requests_panel.dart, onboarding_wizard.dart,
// facilities_view.dart). Each is drawn in the section's own style, on the section.

const INPUT = 'flex:1 1 160px;min-width:0;height:30px;padding:0 10px;border:1px solid rgba(34,39,46,.2);border-radius:7px;background:#FBF8F1;font-size:16px;font-family:inherit;color:#22272E;box-sizing:border-box;';
const ROW = 'display:flex;align-items:center;gap:8px 12px;flex-wrap:wrap;padding:9px 0;border-top:1px solid rgba(34,39,46,.07);min-width:0;';
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** The one-time link an approval or an invite produces, to copy and send. */
export function InviteLink({ url, who }: { url: string; who: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div data-testid="invite-link" style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;padding:8px 0;min-width:0;')}>
      <span style={sx('font-size:16px;color:#54606C;flex:0 0 auto;')}>{I('Invite link for ' + who + ' · expires in 7 days')}</span>
      <code style={sx('flex:1 1 220px;min-width:0;font-size:16px;color:#22272E;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;user-select:all;')}>{url}</code>
      <a
        href="#"
        onClick={(e) => {
          pre(e);
          void navigator.clipboard?.writeText(url).then(() => setCopied(true), () => setCopied(false));
        }}
        style={sx(btn(copied ? 'soft' : 'ghost'))}
      >
        {copied ? 'Copied' : 'Copy link'}
      </a>
    </div>
  );
}

/** Pending public "Request access" forms: Approve makes the operator and its owner's invite; Deny closes it. */
export function AccessRequestRows({ requests, onChanged }: { requests: AccessRequestsResponse['requests']; onChanged: () => void }) {
  const { client } = useSession();
  const { wr } = useGuarded();
  const [links, setLinks] = useState<Record<string, { url: string; pending: boolean }>>({});
  const [err, setErr] = useState<ApiError | null>(null);
  const [deny, setDeny] = useState<AccessRequestsResponse['requests'][number] | null>(null);
  if (!requests.length && !Object.keys(links).length) return null;
  return (
    <div data-testid="access-requests" style={sx('margin-top:10px;')}>
      {requests.map((r) => (
        <div key={r.id} style={sx(ROW)}>
          <span style={sx('flex:1 1 240px;min-width:0;')}>
            <span style={sx('display:block;font-size:16px;font-weight:700;color:#22272E;')}>{I(r.org_name)}</span>
            <span style={sx('display:block;font-size:16px;color:#54606C;margin-top:2px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;')}>
              {I([r.contact_name, r.email, r.facility_count ? r.facility_count + ' buildings' : null, r.role_wanted, r.pilot_code ? 'code ' + r.pilot_code : null].filter(Boolean).join(' · '))}
            </span>
            {r.note && <span style={sx('display:block;font-size:16px;color:#3C454F;margin-top:3px;text-wrap:pretty;')}>{I(r.note)}</span>}
          </span>
          <a
            href="#"
            {...wr(() => void (async () => {
              setErr(null);
              try {
                const out = await accessRequestApprove(client, { params: { id: r.id } });
                setLinks((l) => ({ ...l, [r.id]: { url: out.inviteUrl, pending: !!out.pendingApproval } }));
                onChanged();
              } catch (e) {
                setErr(e as ApiError);
              }
            })())}
            style={sx(btn('primary'))}
          >
            Approve
          </a>
          <a href="#" {...wr(() => setDeny(r))} style={sx(btn('ghost'))}>Deny</a>
        </div>
      ))}
      {Object.entries(links).map(([id, l]) => (
        <div key={id}>
          {l.pending ? (
            <div style={sx('font-size:16px;color:#54606C;padding:6px 0;')}>Approved. The owner's invite waits in the approval queue until Aithora approves it; the link works after that.</div>
          ) : null}
          <InviteLink url={l.url} who="the new owner" />
        </div>
      ))}
      <ErrLine error={err} style="margin-top:6px;" />
      {deny && (
        <ConfirmDialog
          title={`Deny ${deny.org_name}?`}
          body="The request is closed and no account is made. They can ask again from the public form."
          confirmLabel="Deny"
          danger
          onConfirm={async () => {
            await accessRequestDeny(client, { params: { id: deny.id } });
            onChanged();
          }}
          onClose={() => setDeny(null)}
        />
      )}
    </div>
  );
}

/**
 * The onboarding wizard: find the building in the CMS catalog, confirm it, name its first
 * administrator, and send. It posts to POST /admin/facilities, which makes the building
 * and the admin's one-time invite; the link comes back here to copy.
 */
export function OnboardWizard({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { client } = useSession();
  const { wr } = useGuarded();
  const [q, setQ] = useState('');
  const [state, setState] = useState('');
  const [hits, setHits] = useState<(CatalogFacility & { ccn?: string | null })[] | null>(null);
  const [pick, setPick] = useState<(CatalogFacility & { ccn?: string | null }) | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);
  const [sent, setSent] = useState<{ url: string | null; pending: boolean } | null>(null);
  const existing = useApi((c, s) => adminFacilities(c, { signal: s }), []);
  const onPlatform = new Set((existing.data?.facilities ?? []).map((f) => f.ccn).filter(Boolean) as string[]);

  const search = async () => {
    if (q.trim().length < 2) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await intelFacilities(client, { query: { q: q.trim(), ...(state.trim() ? { state: state.trim().toUpperCase() } : {}), unitType: 'residential', limit: 8 } });
      setHits((r.facilities ?? []) as (CatalogFacility & { ccn?: string | null })[]);
    } catch (e) {
      setErr(e as ApiError);
    } finally {
      setBusy(false);
    }
  };

  const ready = !!pick && name.trim().length > 1 && EMAIL.test(email.trim());
  const dupe = !!pick?.ccn && onPlatform.has(pick.ccn);
  const send = async () => {
    if (!pick || !ready || dupe) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await facilityOnboard(client, {
        body: { name: pick.name, ccn: pick.ccn ?? undefined, bedCount: pick.beds ?? undefined, adminEmail: email.trim(), adminName: name.trim() },
      });
      setSent({ url: r.inviteUrl ?? null, pending: !!r.adminInvitePendingApproval });
      onDone();
    } catch (e) {
      setErr(e as ApiError);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="onboard-wizard" style={sx('margin-top:10px;padding-top:10px;border-top:1px solid rgba(34,39,46,.1);')}>
      {sent ? (
        <>
          <div style={sx('font-size:16px;font-weight:700;color:#22272E;')}>{I('Invite ready for ' + email.trim())}</div>
          <div style={sx('font-size:16px;color:#54606C;margin-top:3px;text-wrap:pretty;')}>
            {I(sent.pending
              ? (pick?.name ?? 'The building') + ' is in onboarding. The admin invite waits in the approval queue until Aithora approves it; the link works after that.'
              : (pick?.name ?? 'The building') + ' is in onboarding. Nothing is emailed: send them this link yourself. It signs them into their own building and expires in 7 days.')}
          </div>
          {sent.url && <InviteLink url={sent.url} who={name.trim() || email.trim()} />}
          <a href="#" onClick={(e) => { pre(e); onClose(); }} style={sx(btn('ghost') + 'margin-top:6px;')}>Done</a>
        </>
      ) : !pick ? (
        <>
          <div style={sx(CAPT + 'margin-bottom:7px;')}>Find the building</div>
          <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;')}>
            <input type="text" aria-label="State" placeholder="State" value={state} maxLength={2} onChange={(e) => setState(e.target.value)} style={sx(INPUT + 'flex:0 0 64px;')} />
            <input type="text" aria-label="Search the CMS catalog" placeholder="Name or city" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void search(); } }} style={sx(INPUT)} />
            <a href="#" onClick={(e) => { pre(e); void search(); }} aria-disabled={busy || undefined} style={sx(btn('primary'))}>Search</a>
            <a href="#" onClick={(e) => { pre(e); onClose(); }} style={sx(btn('ghost'))}>Cancel</a>
          </div>
          <div style={sx('font-size:16px;color:#54606C;margin-top:6px;text-wrap:pretty;')}>You confirm a match from the live CMS catalog; the building is never typed in by hand.</div>
          {hits && hits.length === 0 && <div style={sx('font-size:16px;color:#54606C;padding:10px 0;')}>{I('Nothing in the catalog matches "' + q.trim() + '".')}</div>}
          {(hits ?? []).map((h) => {
            const on = !!h.ccn && onPlatform.has(h.ccn);
            return (
              <a key={h.id} href="#" onClick={(e) => { pre(e); setPick(h); }} style={sx(ROW + 'text-decoration:none;color:inherit;')}>
                <span style={sx('flex:1 1 220px;min-width:0;')}>
                  <span style={sx('display:block;font-size:16px;font-weight:700;')}>{I(h.name)}</span>
                  <span style={sx('display:block;font-size:16px;color:#54606C;margin-top:2px;')}>{I([h.city && h.state ? h.city + ', ' + h.state : h.state, h.beds ? h.beds + ' beds' : null, h.ccn ? 'CCN ' + h.ccn : null].filter(Boolean).join(' · '))}</span>
                </span>
                {on && <span style={sx(chip('#5B6B7C', 'rgba(91,107,124,.16)'))}>ON PLATFORM</span>}
              </a>
            );
          })}
        </>
      ) : (
        <>
          <div style={sx(CAPT + 'margin-bottom:7px;')}>{I(dupe ? pick.name + ' is already on the platform' : 'Is this the building? ' + pick.name)}</div>
          <div style={sx('font-size:16px;color:#54606C;margin-bottom:9px;')}>
            {I(dupe
              ? 'It already has a tenant, so adding it again would split its residents across two records.'
              : [pick.city && pick.state ? pick.city + ', ' + pick.state : null, pick.beds ? pick.beds + ' certified beds' : null, pick.ccn ? 'CCN ' + pick.ccn : null].filter(Boolean).join(' · '))}
          </div>
          {!dupe && (
            <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:7px;')}>
              <input type="text" aria-label="Administrator name" placeholder="Their administrator's name" value={name} onChange={(e) => setName(e.target.value)} style={sx(INPUT)} />
              <input type="email" aria-label="Administrator email" placeholder="Work email" value={email} onChange={(e) => setEmail(e.target.value)} style={sx(INPUT)} />
              <a href="#" {...wr(() => void send())} aria-disabled={!ready || busy || undefined} style={sx(btn(ready ? 'primary' : 'off'))}>
                {ready ? 'Send invite' : name.trim().length > 1 ? 'Enter a valid work email' : 'Enter their name and email'}
              </a>
            </div>
          )}
          <a href="#" onClick={(e) => { pre(e); setPick(null); }} style={sx(btn('ghost') + 'margin-top:8px;')}>Back</a>
        </>
      )}
      <ErrLine error={err} style="margin-top:6px;" />
    </div>
  );
}

const BAA_STEPS = ['none', 'sent', 'signed', 'countersigned'] as const;
type BaaStep = (typeof BAA_STEPS)[number];
const BAA_LABEL: Record<BaaStep, string> = { none: 'None', sent: 'Sent', signed: 'Signed', countersigned: 'Countersigned' };

/**
 * Where a building's BAA stands, recorded by Aithora (POST /onboarding/facility/:id/baa).
 * The facility signs the paper; Aithora records it. "Countersigned" means signed by both
 * sides: it ticks the Facility Admin's "Sign the BAA" step and is what lets residents be
 * loaded (the server refuses them before it, src/aithora/baa-gate.ts). HIPAA (45 CFR
 * 164.502(e)) allows no resident data to reach Aithora before then, and the signed copy
 * must be producible later, so recording it asks who signed for each side, the effective
 * date and where the signed copy is kept. Who recorded it and when go in the audit log.
 */
function BaaControl({ f, onChanged }: { f: { id: string; name: string; baa_status: string | null }; onChanged: () => void }) {
  const { client } = useSession();
  const { canWrite, roReason } = useGuarded();
  const [next, setNext] = useState<BaaStep | null>(null);
  const [rec, setRec] = useState({ bothSigned: false, facilitySigner: '', aithoraSigner: '', effectiveDate: new Date().toISOString().slice(0, 10), documentRef: '' });
  const cur = (BAA_STEPS as readonly string[]).includes(f.baa_status ?? '') ? (f.baa_status as BaaStep) : 'none';
  const field = (label: string, k: 'facilitySigner' | 'aithoraSigner' | 'effectiveDate' | 'documentRef', placeholder: string) => (
    <label style={sx('display:block;font-size:16px;color:#3C454F;margin-top:8px;')}>
      {label}
      <input
        type={k === 'effectiveDate' ? 'date' : 'text'}
        aria-label={label}
        value={rec[k]}
        placeholder={placeholder}
        onChange={(e) => setRec({ ...rec, [k]: e.target.value })}
        style={sx(INPUT + 'display:block;width:100%;margin-top:3px;')}
      />
    </label>
  );
  return (
    <>
      <select
        aria-label={`BAA status for ${f.name}`}
        value={cur}
        disabled={!canWrite}
        title={roReason ?? 'Where this building’s BAA stands'}
        onChange={(e) => setNext(e.target.value as BaaStep)}
        style={sx('height:28px;border-radius:7px;border:1px solid rgba(34,39,46,.18);background:#FCFAF4;font-size:16px;padding:0 6px;color:#22272E;')}
      >
        {BAA_STEPS.map((b) => (
          <option key={b} value={b}>{'BAA · ' + BAA_LABEL[b]}</option>
        ))}
      </select>
      {next && (
        <ConfirmDialog
          title={`Record ${f.name}'s BAA as ${BAA_LABEL[next].toLowerCase()}?`}
          body={next === 'countersigned' ? (
            <>
              <div>Countersigned means both sides have signed. It ticks their setup step, and their residents can then be loaded. Until then, residents are refused.</div>
              <label style={sx('display:flex;align-items:flex-start;gap:8px;font-size:16px;color:#22272E;margin-top:10px;')}>
                <input type="checkbox" aria-label="Signed by both sides" checked={rec.bothSigned} onChange={(e) => setRec({ ...rec, bothSigned: e.target.checked })} />
                <span>The BAA is signed by both sides, and I have the signed copy.</span>
              </label>
              {field('Signed for the building by', 'facilitySigner', 'Name and title, as on the agreement')}
              {field('Signed for Aithora by', 'aithoraSigner', 'Name, as on the agreement')}
              {field('Effective date', 'effectiveDate', 'YYYY-MM-DD')}
              {field('Where the signed copy is kept', 'documentRef', 'File name or link')}
              <div style={sx('font-size:16px;color:#54606C;margin-top:8px;')}>Your name and the time are recorded with it in the audit log.</div>
            </>
          ) : (
            'This records where the agreement stands. Residents cannot be loaded until it is signed by both sides.'
          )}
          confirmLabel="Record"
          onConfirm={async () => {
            await facilityBaaStatus(client, { params: { id: f.id }, body: next === 'countersigned' ? { status: next, ...rec } : { status: next } });
            onChanged();
          }}
          onClose={() => setNext(null)}
        />
      )}
    </>
  );
}

/** Every building and operator on the platform (the Flutter Facilities page's list). */
export function BuildingsList() {
  const { realRole } = useConsoleRole();
  const aithora = realRole === 'SUPER_ADMIN' || realRole === 'OPERATOR';
  const list = useApi((c, s) => adminFacilities(c, { signal: s }), []);
  const [rolesFor, setRolesFor] = useState<string | null>(null);
  const rows = list.data?.facilities ?? [];
  if (!rows.length) return null;
  return (
    <div data-testid="settings-buildings" style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:9px 2px;margin-bottom:12px;')}>
      <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;margin-bottom:6px;')}>
        <span style={sx('width:7px;height:7px;border-radius:50%;background:#2F5C97;')} />
        <span style={sx('font-size:16px;font-weight:700;')}>Buildings</span>
        <span style={sx('flex:1;')} />
        <span style={sx('font-size:16px;color:#54606C;')}>{I(rows.filter((r) => r.org_type === 'facility').length + ' buildings · ' + rows.filter((r) => r.org_type === 'operator').length + ' operators')}</span>
        <HouseholdCreate />
      </div>
      {rows.map((f) => (
        <div key={f.id} style={sx(ROW)}>
          <span style={sx('flex:1 1 220px;min-width:0;')}>
            <span style={sx('display:block;font-size:16px;font-weight:700;color:#22272E;')}>{I(f.name)}</span>
            <span style={sx('display:block;font-size:16px;color:#54606C;margin-top:2px;')}>
              {I([f.org_type === 'operator' ? 'operator' : 'building', f.ccn ? 'CCN ' + f.ccn : null, f.residents + ' residents', f.staff + ' staff', f.pilot_end_date ? 'pilot to ' + String(f.pilot_end_date).slice(0, 10) : null].filter(Boolean).join(' · '))}
            </span>
          </span>
          <span style={sx(chip(f.status === 'active' ? '#12503C' : '#54606C', f.status === 'active' ? 'rgba(47,138,111,.16)' : 'rgba(34,39,46,.09)'))}>{I(String(f.status).toUpperCase())}</span>
          {aithora && f.org_type === 'facility' && <BaaControl f={f} onChanged={list.reload} />}
          <a href="#" aria-expanded={rolesFor === f.id} onClick={(e) => { pre(e); setRolesFor(rolesFor === f.id ? null : f.id); }} style={sx(btn('ghost'))}>
            Roles
          </a>
          {rolesFor === f.id && (
            <div style={sx('flex-basis:100%;min-width:0;')}>
              <CustomRolesPanel orgId={f.id} orgName={f.name} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Sign a person out of every session (confirm-first). Suspend, Resume and Delete live in the
 * Account row under it (AccountDeletion.tsx, the V4 design's "Suspend and delete").
 */
export function UserAccountActions({ user, sessions, onChanged }: { user: { id: string; name: string; status: string }; sessions: number; onChanged: () => void }) {
  const { client } = useSession();
  const { wr } = useGuarded();
  const [ask, setAsk] = useState(false);
  return (
    <>
      {sessions > 0 && (
        <a href="#" {...wr(() => setAsk(true))} style={sx(btn('ghost'))}>
          Sign out everywhere
        </a>
      )}
      {ask && (
        <ConfirmDialog
          title={'Sign ' + user.name + ' out everywhere?'}
          body="Every session they have ends now. They can sign in again."
          confirmLabel="Sign out"
          danger
          onConfirm={async () => {
            await adminUserLogout(client, { params: { id: user.id } });
            onChanged();
          }}
          onClose={() => setAsk(false)}
        />
      )}
    </>
  );
}

const ROLE_WORD: Record<string, string> = { ADMIN: 'Facility Admin', CAREGIVER: 'Care Staff', STAFF: 'Care Staff' };

/**
 * People an Operator added (an onboarding admin invite, a roster import) wait for the
 * owner before they can sign in (GET /approvals). The phone already shows this queue;
 * here the owner (or the deputy) approves them where they were added, one at a time or a
 * whole building's import at once. An Operator sees the list read-only: the owner approves.
 */
export function PendingPeople({ onChanged }: { onChanged: () => void }) {
  const { client } = useSession();
  const { wr } = useGuarded();
  const { realRole } = useConsoleRole();
  // The queue answers the owner, the deputy and an Operator (read-only); nobody else asks.
  const q = useApi((c, s) => approvalsQueue(c, { signal: s }), [], realRole === 'SUPER_ADMIN' || realRole === 'OPERATOR');
  const [err, setErr] = useState<ApiError | null>(null);
  const [all, setAll] = useState<{ id: string; name: string; ids: string[] } | null>(null);
  const [decline, setDecline] = useState<PendingPerson | null>(null);
  const [busy, setBusy] = useState(false);
  const people = q.data?.people ?? [];
  if (!people.length) return null;
  const decides = q.data?.as === 'owner' || q.data?.as === 'deputy';
  const byBuilding = new Map<string, { name: string; people: PendingPerson[] }>();
  for (const p of people) {
    const b = byBuilding.get(p.facility.id) ?? { name: p.facility.name ?? 'A building', people: [] };
    b.people.push(p);
    byBuilding.set(p.facility.id, b);
  }
  const approve = async (ids: string[]) => {
    setErr(null);
    setBusy(true);
    try {
      for (const id of ids) await approvalPersonApprove(client, { params: { id }, body: {} });
    } finally {
      setBusy(false);
      q.reload();
      onChanged();
    }
  };
  return (
    <div data-testid="pending-people" style={sx('margin-top:10px;')}>
      <div style={sx(CAPT + 'margin:4px 0 2px;')}>{I('Waiting for the owner’s approval · ' + people.length)}</div>
      <div style={sx('font-size:16px;color:#54606C;margin-bottom:4px;text-wrap:pretty;')}>
        {I(decides ? 'An Operator added these people. They cannot sign in until you approve them.' : 'They can sign in once the owner approves them.')}
      </div>
      {[...byBuilding.entries()].map(([fid, b]) => (
        <div key={fid} style={sx('padding:4px 0 6px;')}>
          <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;padding:6px 0;')}>
            <span style={sx('font-size:16px;font-weight:700;color:#22272E;flex:1 1 200px;min-width:0;')}>{I(b.name + ' · ' + b.people.length + (b.people.length === 1 ? ' person' : ' people'))}</span>
            {decides && b.people.length > 1 && (
              <a href="#" {...wr(() => setAll({ id: fid, name: b.name, ids: b.people.map((p) => p.id) }))} aria-disabled={busy || undefined} style={sx(btn('primary'))}>
                {I('Approve all ' + b.people.length)}
              </a>
            )}
          </div>
          {b.people.slice(0, 8).map((p) => (
            <div key={p.id} style={sx(ROW)}>
              <span style={sx('flex:1 1 220px;min-width:0;')}>
                <span style={sx('display:block;font-size:16px;font-weight:600;color:#22272E;')}>{I(p.name || p.email || 'Someone')}</span>
                <span style={sx('display:block;font-size:16px;color:#54606C;margin-top:2px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;')}>
                  {I([ROLE_WORD[p.role] ?? p.role, p.email, p.requestedBy?.name ? 'added by ' + p.requestedBy.name : null].filter(Boolean).join(' · '))}
                </span>
              </span>
              {decides && (
                <>
                  <a href="#" {...wr(() => void approve([p.id]).catch((e) => setErr(e as ApiError)))} aria-disabled={busy || undefined} style={sx(btn('ghost'))}>Approve</a>
                  <a href="#" {...wr(() => setDecline(p))} style={sx(btn('ghost'))}>Decline</a>
                </>
              )}
            </div>
          ))}
          {b.people.length > 8 && <div style={sx('font-size:16px;color:#54606C;padding:6px 0;')}>{I('and ' + (b.people.length - 8) + ' more')}</div>}
        </div>
      ))}
      <ErrLine error={err} style="margin-top:6px;" />
      {all && (
        <ConfirmDialog
          title={`Approve all ${all.ids.length} at ${all.name}?`}
          body="Each of them can sign in once approved. Every approval is recorded under your name."
          confirmLabel={`Approve ${all.ids.length}`}
          onConfirm={async () => { await approve(all.ids); }}
          onClose={() => setAll(null)}
        />
      )}
      {decline && (
        <ConfirmDialog
          title={`Decline ${decline.name || decline.email || 'this person'}?`}
          body="Their account is not opened. The reason is kept with the decision."
          confirmLabel="Decline"
          danger
          reason={{ label: 'Why', placeholder: 'Why not? (required)' }}
          onConfirm={async (why) => {
            await approvalPersonDecline(client, { params: { id: decline.id }, body: { reason: why } });
            q.reload();
            onChanged();
          }}
          onClose={() => setDecline(null)}
        />
      )}
    </div>
  );
}
